import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Select, Button, Space, Tooltip, Form } from '@app/ui';
import { capitalize } from '@app/utils/capitalize';
import InputTag from '@app/components/filters/components/InputTag';
import sportsApi from '@app/utils/sportsApi';
import { FB, IG } from '@app/constants/platformIds';
import getTruthyKeys from '@app/utils/getTruthyKeys';

const { Option } = Select;

interface PlatformOption {
    value: string | number;
    name: string;
    label?: string;
}

interface SocialPlatformSelection {
    platformId: string | number | null;
    names: string[];
}

interface SocialHandlePickerProps {
    options: PlatformOption[]; // This is now the platforms array directly
    param: string;
    setSelectedParam: ({
        param,
        sub,
    }: {
        param: string;
        sub?: string;
    }) => (value: any) => void;
    selectedParams: SocialPlatformSelection[];
    disabled?: boolean;
    clientId: number;
    socialPlatformSelections: SocialPlatformSelection;
    socialHandlerIndex: number;
}

const SocialHandlePicker: React.FC<SocialHandlePickerProps> = ({
    options,
    param,
    setSelectedParam,
    selectedParams,
    disabled = false,
    clientId,
    socialPlatformSelections,
    socialHandlerIndex,
}) => {
    const { t } = useTranslation(['tools', 'common']);
    const [authenticatedAccounts, setAuthenticatedAccounts] = useState<
        Array<{ label: string; value: string }>
    >([]);
    const { platformId, names } = socialPlatformSelections;
    const shouldDisplayMultiSelect = [FB, IG].includes(Number(platformId));

    useEffect(() => {
        if (
            shouldDisplayMultiSelect &&
            authenticatedAccounts.length === 0 &&
            platformId
        ) {
            fetchAuthenticatedUsers(platformId);
        }
    }, [platformId, shouldDisplayMultiSelect, authenticatedAccounts.length]);

    const platformOptions = useMemo(() => {
        // options is now the platforms array directly
        return options || [];
    }, [options]);

    const addRow = () => {
        const newSelected = [
            ...selectedParams,
            { platformId: null, names: [] },
        ];
        setSelectedParam({ param })(newSelected);
    };

    const fetchAuthenticatedUsers = async (platformId: string | number) => {
        try {
            const accountsResponse = await sportsApi
                .url(
                    `/clients/${clientId}/authorized-accounts?platformId=${platformId}`,
                )
                .get()
                .json();

            const accountOptions = (accountsResponse as any[]).map(
                (data: any) => ({
                    label: data.account.username,
                    value: data.account.username,
                }),
            );
            setAuthenticatedAccounts(accountOptions);
        } catch (error) {
            console.error('Error fetching authenticated users:', error);
        }
    };

    const getFormattedNames = (names: string[]) => {
        const formattedNames: Record<string, boolean> = {};
        names.forEach(name => (formattedNames[name] = true));
        return formattedNames;
    };

    const formattedNames = shouldDisplayMultiSelect
        ? getFormattedNames(names)
        : names;

    const setPlatform = (pId: string | number) => {
        const newSelected = selectedParams.map((socialHandle, i) => {
            if (i !== socialHandlerIndex) return socialHandle;
            return { names: [], platformId: pId };
        });
        setSelectedParam({ param })(newSelected);
    };

    // used in InputTag component
    const setNames = (names: string[]) => {
        const newSelected = selectedParams.map((socialHandle, i) => {
            if (i !== socialHandlerIndex) return socialHandle;
            return { ...socialHandle, names: names };
        });
        setSelectedParam({ param })(newSelected);
    };

    // used in MultiSelect
    const setNameSelections = (names: Record<string, boolean>) => {
        const newSelected = selectedParams.map((socialHandle, i) => {
            if (i !== socialHandlerIndex) return socialHandle;
            return { ...socialHandle, names: getTruthyKeys(names) };
        });
        setSelectedParam({ param })(newSelected);
    };

    const deleteRow = () => {
        if (selectedParams.length <= 1) return;
        const newSelected = selectedParams.filter(
            (sel, i) => i !== socialHandlerIndex,
        );
        setSelectedParam({ param })(newSelected);
    };

    const hasPlatform = !!platformId;
    const isLast = socialHandlerIndex === selectedParams.length - 1;
    const isFirst = socialHandlerIndex === 0;

    const platformName =
        platformId && platformOptions.find(p => p.value === platformId)?.name;

    const hiddenPlatforms = selectedParams.reduce(
        (acc: (string | number)[], select) => {
            if (!select.platformId || select.platformId === platformId)
                return acc;
            acc.push(select.platformId);
            return acc;
        },
        [],
    );

    const deleteDisabled = disabled || selectedParams.length <= 1;
    const addRowDisabled =
        disabled ||
        !selectedParams.every(sel => sel.platformId && sel.names.length > 0) ||
        hiddenPlatforms.length >= platformOptions.length - 1;

    return (
        <Row
            gutter={16}
            align="middle"
            style={{ marginTop: isFirst ? 0 : 8, width: '100%' }}
        >
            <Col flex="200px">
                {!disabled && (
                    <Select
                        style={{ width: '100%' }}
                        placeholder={t(
                            'tools:automatedParams.placeholders.platform',
                        )}
                        value={platformId}
                        onChange={setPlatform}
                        disabled={disabled}
                    >
                        {platformOptions
                            .filter(
                                option =>
                                    !hiddenPlatforms.includes(option.value),
                            )
                            .map(option => (
                                <Option key={option.value} value={option.value}>
                                    {option.name || option.label}
                                </Option>
                            ))}
                    </Select>
                )}
                {disabled && platformId && (
                    <Button
                        type="primary"
                        style={{ width: '100%' }}
                        disabled={disabled}
                    >
                        {`${capitalize(platformName || '')}`}
                    </Button>
                )}
            </Col>
            <Col flex="1">
                {hasPlatform && (
                    <Space style={{ width: '100%' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                            @
                        </span>
                        <Form.Item style={{ margin: 0, flex: 1 }}>
                            {shouldDisplayMultiSelect ? (
                                <Select
                                    mode="multiple"
                                    style={{ width: '100%', minWidth: 260 }}
                                    placeholder={t(
                                        'tools:automatedParams.placeholders.users',
                                    )}
                                    value={Object.keys(formattedNames)}
                                    onChange={(values: string[]) => {
                                        const formattedValues: Record<
                                            string,
                                            boolean
                                        > = {};
                                        values.forEach(
                                            value =>
                                                (formattedValues[value] = true),
                                        );
                                        setNameSelections(formattedValues);
                                    }}
                                    options={authenticatedAccounts}
                                    dropdownMatchSelectWidth={false}
                                    dropdownStyle={{ minWidth: 320 }}
                                />
                            ) : (
                                <InputTag
                                    value={
                                        Array.isArray(formattedNames)
                                            ? formattedNames
                                            : []
                                    }
                                    placeholderText={
                                        !disabled
                                            ? t(
                                                  'tools:automatedParams.placeholders.enter',
                                              )
                                            : ''
                                    }
                                    onChange={setNames}
                                    maxCount={disabled ? 0 : undefined}
                                    isHandleInput
                                />
                            )}
                        </Form.Item>
                        <Tooltip
                            title={
                                shouldDisplayMultiSelect
                                    ? t(
                                          'tools:automatedParams.tooltips.socialMultiSelectHelper',
                                      )
                                    : t(
                                          'tools:automatedParams.tooltips.socialHandleHelper',
                                      )
                            }
                        >
                            <i className="fas fa-info-circle" />
                        </Tooltip>
                    </Space>
                )}
            </Col>
            <Col flex="80px">
                <Space size="small">
                    {!deleteDisabled && (
                        <Tooltip
                            title={t(
                                'tools:automatedParams.tooltips.removeSocialHandle',
                            )}
                        >
                            <Button
                                type="text"
                                danger
                                icon={<i className="far fa-times" />}
                                onClick={deleteRow}
                                disabled={deleteDisabled}
                                size="small"
                            />
                        </Tooltip>
                    )}
                    {isLast && !disabled && (
                        <Tooltip
                            title={t(
                                'tools:automatedParams.tooltips.addSocialHandle',
                            )}
                        >
                            <Button
                                type="text"
                                icon={<i className="far fa-plus" />}
                                onClick={addRow}
                                disabled={addRowDisabled}
                                size="small"
                                style={{
                                    color: addRowDisabled ? 'gray' : 'green',
                                }}
                            />
                        </Tooltip>
                    )}
                </Space>
            </Col>
        </Row>
    );
};

export default SocialHandlePicker;
